/**
 * ZAdminCueOrgs - Zod Validation Schemas for Admin Cue Orgs API
 *
 * Per-org Cue workspace-mount config: GET / PATCH the
 * `OrganizationCueConfig.workspaceMode` toggle.
 *
 * Mode semantics (mirrors the Prisma `CueWorkspaceMode` enum). The mode
 * decides what the mount's STS session credentials allow; nothing inside the
 * sandbox enforces it, so every refusal below is S3's and belongs to a mount
 * that attached.
 *   - NONE        → Cue sandbox gets no sandbox mount
 *   - READ        → mounted with credentials carrying `s3:GetObject` and no
 *                   write action, so S3 refuses the upload on flush
 *   - READ_WRITE  → the org's OWN storage is mounted writable; a shared-library
 *                   workspace stays read unless the org also holds a write
 *                   grant on it — the mode caps shared access, never grants it
 *
 * Endpoints are PBAC-protected (admin:cue).
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// SHARED SCHEMAS
// ═══════════════════════════════════════════════════════════════

/** Mirror of the Prisma `CueWorkspaceMode` enum. Kept here so the
 *  shared types package doesn't depend on the Prisma client at runtime. */
export const AdminCueWorkspaceModeSchema = z.enum(["NONE", "READ", "READ_WRITE"]);

export type AdminCueWorkspaceMode = z.infer<typeof AdminCueWorkspaceModeSchema>;

/** Single per-org config row, post-toJSON. `updatedAt` is ISO-8601;
 *  the entity returns the epoch (`1970-01-01T...`) for orgs that have no
 *  row yet, which the UI treats as "never set". */
export const AdminCueConfigSchema = z.object({
  workspaceMode: AdminCueWorkspaceModeSchema,
  updatedAt: z.string()
});

export type AdminCueConfig = z.infer<typeof AdminCueConfigSchema>;

// ═══════════════════════════════════════════════════════════════
// REQUEST / RESPONSE SCHEMAS
// ═══════════════════════════════════════════════════════════════

export const AdminCueGetModeResponseSchema = AdminCueConfigSchema;

export const AdminCueSetModeBodySchema = z.object({
  workspaceMode: AdminCueWorkspaceModeSchema
});

export const AdminCueSetModeResponseSchema = AdminCueConfigSchema;

// ═══════════════════════════════════════════════════════════════
// PATH VARS SCHEMAS
// ═══════════════════════════════════════════════════════════════

export const AdminCueOrgPathVarsSchema = z.object({
  orgId: z.string().min(1)
});

// ═══════════════════════════════════════════════════════════════
// ZADMINCUEORGS MANIFEST
// ═══════════════════════════════════════════════════════════════

export const ZAdminCueOrgs = {
  /**
   * Read the org's Cue workspace-mount mode.
   * Returns the default (mode = NONE, updatedAt = epoch) when no row exists.
   * GET /api/admin/organizations/:orgId/cue
   */
  GetMode: {
    method: "GET" as const,
    path: "/api/admin/organizations/:orgId/cue",
    PathVars: AdminCueOrgPathVarsSchema,
    Response: ApiSuccessSchema(AdminCueGetModeResponseSchema)
  },

  /**
   * Upsert the org's Cue workspace-mount mode.
   * Takes effect on the next sandbox creation + the next streaming turn —
   * existing Cue sandboxes keep their original mount until they recycle.
   * PATCH /api/admin/organizations/:orgId/cue
   */
  SetMode: {
    method: "PATCH" as const,
    path: "/api/admin/organizations/:orgId/cue",
    PathVars: AdminCueOrgPathVarsSchema,
    Body: AdminCueSetModeBodySchema,
    Response: ApiSuccessSchema(AdminCueSetModeResponseSchema)
  }
} as const;

// ═══════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════

export type AdminCueOrgPathVars = z.infer<typeof AdminCueOrgPathVarsSchema>;
export type AdminCueGetModeResponse = z.infer<typeof AdminCueGetModeResponseSchema>;
export type AdminCueSetModeBody = z.infer<typeof AdminCueSetModeBodySchema>;
export type AdminCueSetModeResponse = z.infer<typeof AdminCueSetModeResponseSchema>;
